import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Users2, BadgeCheck, PhoneCall, IndianRupee, Loader2, FileSpreadsheet } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { inr } from "@/lib/calc";
import { exportTeamXlsx } from "@/lib/export";

const ymd = (d) => d.toLocaleDateString("en-CA");
const shiftDay = (d, n) => { const x = new Date(d + "T00:00:00"); x.setDate(x.getDate() + n); return ymd(x); };
const shiftMonth = (m, n) => { const [y, mo] = m.split("-").map(Number); const x = new Date(y, mo - 1 + n, 1); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`; };
const monthRange = (m) => { const [y, mo] = m.split("-").map(Number); return [`${m}-01`, ymd(new Date(y, mo, 0))]; };
const COLORS = { counselling: "#4a2844", admissions: "#b97fa9", followups: "#d4a017" };

const Kpi = ({ icon: Icon, label, value, testid, cls }) => (
  <div className="rounded-lg border border-plum-900/10 bg-white px-5 py-4">
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-500"><Icon className="h-3.5 w-3.5" />{label}</div>
    <div className={`font-num mt-1.5 text-2xl font-semibold ${cls || "text-plum-950"}`} data-testid={testid}>{value}</div>
  </div>
);

const Cell = ({ v, max, color, testid }) => (
  <td className="px-4 py-3">
    <div className="flex items-center justify-end gap-2">
      <div className="hidden h-1.5 w-20 rounded-full bg-slate-100 sm:block"><div className="h-1.5 rounded-full" style={{ width: `${max ? (v / max) * 100 : 0}%`, background: color }} /></div>
      <span className="font-num w-8 text-right font-semibold" data-testid={testid}>{v}</span>
    </div>
  </td>
);

const TeamTable = ({ team }) => {
  const max = (k) => Math.max(0, ...team.map((t) => t[k]));
  return (
    <div className="overflow-x-auto rounded-lg border border-plum-900/10 bg-white" data-testid="team-table">
      <table className="w-full min-w-[760px] text-sm">
        <thead>
          <tr className="border-b border-slate-200 font-bold text-left text-[11px] uppercase tracking-wider text-slate-500">
            <th className="w-10 px-4 py-3">#</th><th className="px-4 py-3">Counsellor</th>
            <th className="px-4 py-3 text-right">Counselling done</th><th className="px-4 py-3 text-right">Admissions closed</th>
            <th className="px-4 py-3 text-right">Follow-ups done</th><th className="px-4 py-3 text-right">Conversion</th>
            <th className="px-4 py-3 text-right">Collected</th>
          </tr>
        </thead>
        <tbody>
          {team.map((t, i) => {
            const slug = t.name.toLowerCase().replace(/\s+/g, "-");
            return (
              <tr key={t.name} className="border-b border-slate-100" data-testid={`team-row-${slug}`}>
                <td className="font-num px-4 py-3 text-slate-400">{i + 1}</td>
                <td className={`px-4 py-3 font-medium ${t.name === "Unassigned" ? "italic text-slate-500" : ""}`}>{t.name}</td>
                <Cell v={t.counselling} max={max("counselling")} color={COLORS.counselling} testid={`team-counselling-${slug}`} />
                <Cell v={t.admissions} max={max("admissions")} color={COLORS.admissions} testid={`team-admissions-${slug}`} />
                <Cell v={t.followups} max={max("followups")} color={COLORS.followups} testid={`team-followups-${slug}`} />
                <td className="font-num px-4 py-3 text-right text-plum-700">{t.conversion === null ? "—" : `${t.conversion}%`}</td>
                <td className="font-num px-4 py-3 text-right">₹ {inr(t.collected)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!team.length && <p className="px-5 py-12 text-center text-sm text-slate-500" data-testid="team-empty">No activity in this period.</p>}
    </div>
  );
};

export default function Team() {
  const [mode, setMode] = useState("daily");
  const [day, setDay] = useState(ymd(new Date()));
  const [month, setMonth] = useState(ymd(new Date()).slice(0, 7));
  const [start, end] = mode === "daily" ? [day, day] : monthRange(month);
  const { data, isFetching } = useQuery({
    queryKey: ["team", start, end],
    queryFn: () => api.get("/reports/team", { params: { start, end } }).then((r) => r.data),
  });
  const team = data?.team || [];
  const sum = (k) => team.reduce((s, t) => s + t[k], 0);
  const label = mode === "daily"
    ? new Date(day + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : new Date(`${month}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  const chart = mode === "monthly" ? (() => {
    const [y, mo] = month.split("-").map(Number);
    const byDay = Object.fromEntries((data?.daily || []).map((d) => [d.date, d]));
    return Array.from({ length: new Date(y, mo, 0).getDate() }, (_, i) => {
      const k = `${month}-${String(i + 1).padStart(2, "0")}`;
      return { day: i + 1, counselling: 0, admissions: 0, followups: 0, ...byDay[k] };
    });
  })() : [];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div className="fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-plum-700" data-testid="team-period-label">{label}</div>
          <h1 className="font-display mt-0.5 text-3xl font-bold leading-tight tracking-tight text-plum-950 sm:text-4xl">Team Performance</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button disabled={!team.length} onClick={() => exportTeamXlsx(team, mode === "monthly" ? chart.map((d) => ({ date: `${month}-${String(d.day).padStart(2, "0")}`, counselling: d.counselling, admissions: d.admissions, followups: d.followups })) : null, label, `Team_Report_${mode === "daily" ? day : month}.xlsx`)}
            className="bg-plum-700 hover:bg-plum-800" data-testid="team-export-btn">
            <FileSpreadsheet className="mr-2 h-4 w-4" />Export Excel
          </Button>
          <div className="flex rounded-lg border border-plum-900/10 bg-white p-1">
            {["daily", "monthly"].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`team-mode-${m}`}
                className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize transition-colors duration-150 ${mode === m ? "bg-plum-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{m}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" onClick={() => (mode === "daily" ? setDay(shiftDay(day, -1)) : setMonth(shiftMonth(month, -1)))} data-testid="team-prev"><ChevronLeft className="h-4 w-4" /></Button>
          {mode === "daily"
            ? <Input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="w-40 bg-white" data-testid="team-day-input" />
            : <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44 bg-white" data-testid="team-month-input" />}
          <Button size="icon" variant="outline" onClick={() => (mode === "daily" ? setDay(shiftDay(day, 1)) : setMonth(shiftMonth(month, 1)))} data-testid="team-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </div>
      </div>

      <div className="fade-up d1 mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi icon={Users2} label="Counselling done" value={sum("counselling")} testid="team-kpi-counselling" />
        <Kpi icon={BadgeCheck} label="Admissions closed" value={sum("admissions")} testid="team-kpi-admissions" cls="text-plum-700" />
        <Kpi icon={PhoneCall} label="Follow-ups done" value={sum("followups")} testid="team-kpi-followups" cls="text-amber-600" />
        <Kpi icon={IndianRupee} label="Collected" value={`₹ ${inr(sum("collected"))}`} testid="team-kpi-collected" />
      </div>

      {mode === "monthly" && (
        <div className="fade-up d2 mb-6 rounded-lg border border-plum-900/10 bg-white p-5" data-testid="team-daily-chart">
          <h2 className="font-display mb-4 text-base font-bold text-plum-950">Day by day</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="counselling" name="Counselling" fill={COLORS.counselling} radius={[2, 2, 0, 0]} />
                <Bar dataKey="admissions" name="Admissions" fill={COLORS.admissions} radius={[2, 2, 0, 0]} />
                <Bar dataKey="followups" name="Follow-ups" fill={COLORS.followups} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="fade-up d2"><TeamTable team={team} /></div>
      <p className="mt-3 text-xs text-slate-500">
        Counselling = receipts issued (by the counsellor named on the receipt). Admissions = receipts marked Admitted, dated by payment date.
        Follow-ups = follow-up updates (status / date / note changes, excluding the admission itself) logged by each staff member. Times in IST.
      </p>
    </div>
  );
}
